import React, { useCallback, useEffect, useRef, useState } from 'react';
import CinziaSide from './CinziaSide';
import AllDesignSide from './AllDesignSide';
import TrailMagnifier from './TrailMagnifier';
import HomeScrollIndicator from './HomeScrollIndicator';
import LogoSlashSVG from '../Intro/LogoSlashSVG';
import SideMenu, { CINZIA_MENU, ALLDESIGN_MENU } from '../Nav/SideMenu';
import LanguageSwitcher from '../Nav/LanguageSwitcher';
import { useLang } from '../../lib/lang';

/**
 * HeroInner
 * ---------
 * Pure visual layer of the split-screen hero. Rendered ONCE as the base by
 * TrailMagnifier + N times inside each trailing lens (each lens gets its
 * own React subtree so videos/media re-mount inside the zoomed clones).
 */
function HeroInner({ activeSide, cinziaSpeed }) {
    const lang = useLang();
    const cinziaTag = lang === 'en' ? 'The Artist · The Brand' : "L'Artista · Il Brand";
    const alldesignTag = lang === 'en' ? 'Studio · Creative Direction' : 'Studio · Creative Direction';
    return (
        <div
            className="split-root"
            data-active={activeSide ?? 'none'}
            data-testid="split-screen"
        >
            {/* Brand corner mark — small "\\" SVG + "ARAIA ALL DESIGN" (no slash separator) */}
            <div className="brand-mark" data-testid="brand-corner-mark">
                <LogoSlashSVG barClassName="" />
                <span>ARAIA ALL DESIGN</span>
            </div>

            {/* Language switch — top-right on the hero */}
            <LanguageSwitcher variant="hero" />

            {/* MEDIA LAYER 1 — Cinzia (full viewport, diagonally clipped).
                The video sits inset:0 at 100vw so `object-fit: cover` samples
                the full viewport aspect ratio — no aggressive crop from a
                narrow 50% panel like before. The clip-path polygon reveals
                only the left diagonal wedge. */}
            <div
                className="media-left"
                data-testid="media-left"
                aria-label="Cinzia Araia — Artist"
            >
                <CinziaSide speed={cinziaSpeed} />
            </div>

            {/* MEDIA LAYER 2 — All Design (full viewport, diagonally clipped) */}
            <div
                className="media-right"
                data-testid="media-right"
                aria-label="All Design Studio — Creative Direction"
            >
                <AllDesignSide />
            </div>

            {/* Soft blend strip along the diagonal seam */}
            <div className="blend-strip" aria-hidden="true" />

            {/* iter97 · Phase 2A — H1 cleanup.
                HeroInner è wrappato da TrailMagnifier che duplica il
                DOM in 5 lens-clones (6 copie totali). Mantenere i due
                `<h1>` qui produceva 12 H1 nel runtime DOM (6×2) con
                Lighthouse flag "duplicate H1". Downgrade a `<h2>` con
                la stessa classe `.side-title` → zero visual change
                (styling class-based, non tag-dependent). L'unico H1
                della pagina è `.sr-only` renderizzato in Home.jsx
                così resta semantico per screen reader e indicizzabile
                dai bot (anche se il SSG ne scrive un altro nel head
                del preview — i bot ne vedono comunque solo 1 nel
                runtime DOM dopo React mount). */}
            <div className="side-content side-content--left" data-testid="cinzia-content">
                <div className="side-header-group">
                    <h2 className="side-title side-title--white">CINZIA<br />ARAIA</h2>
                    <span className="side-tag side-tag--light">
                        {cinziaTag}
                    </span>
                </div>
                <SideMenu world="cinzia" items={CINZIA_MENU} />
            </div>
            <div className="side-content side-content--right" data-testid="alldesign-content">
                <div className="side-header-group">
                    <h2 className="side-title side-title--ink">ALL<br />DESIGN</h2>
                    <span className="side-tag side-tag--dark">
                        {alldesignTag}
                    </span>
                </div>
                <SideMenu world="alldesign" items={ALLDESIGN_MENU} />
            </div>
        </div>
    );
}

/**
 * SplitScreen
 * -----------
 * Two-world hero. TrailMagnifier wraps HeroInner and adds N=5 zoomed lens
 * clones that trail the cursor. Hover-respiro is handled at the container
 * level so it applies to the base HeroInner (the shared `activeSide` state).
 */
export default function SplitScreen() {
    const rootRef = useRef(null);
    const [activeSide, setActiveSide] = useState(null); // 'left' | 'right' | null
    const [cinziaSpeed, setCinziaSpeed] = useState(1);

    const handleMove = useCallback((e) => {
        const root = rootRef.current;
        if (!root) return;
        const rect = root.getBoundingClientRect();
        const isMobile = window.matchMedia('(max-width: 860px)').matches;
        const pct = isMobile
            ? (e.clientY - rect.top) / rect.height
            : (e.clientX - rect.left) / rect.width;
        if (pct < 0.48) setActiveSide('left');
        else if (pct > 0.52) setActiveSide('right');
    }, []);

    const handleLeave = useCallback(() => setActiveSide(null), []);

    useEffect(() => {
        if (activeSide === 'left') setCinziaSpeed(1.15);
        else if (activeSide === 'right') setCinziaSpeed(0.85);
        else setCinziaSpeed(1);
    }, [activeSide]);

    return (
        <div
            ref={rootRef}
            className="split-container"
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            data-testid="split-container"
        >
            <TrailMagnifier count={5} radius={118} zoom={1.22}>
                <HeroInner activeSide={activeSide} cinziaSpeed={cinziaSpeed} />
            </TrailMagnifier>
            <HomeScrollIndicator />
        </div>
    );
}
